import type { DateRange, Matcher } from 'react-day-picker'

import React, { useEffect, useMemo, useState } from 'react'
import {
	addDays,
	addMonths,
	addQuarters,
	addWeeks,
	endOfDay,
	endOfMonth,
	intlFormat,
	isSameDay,
	startOfDay,
	startOfMonth,
} from 'date-fns'
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon } from 'lucide-react'

import { cn } from '@/shared/lib/utils'

import { useDebounce } from '../../hooks/use-debounce'
import { Button } from './button'
import { Calendar } from './calendar'
import { Command, CommandGroup, CommandItem, CommandList } from './command'
import { Input } from './input'

type PeriodUnit = 'm' | 'd' | 'w'
type RelativePeriodUnit = PeriodUnit
type RelativeUnitsMapping = Record<
	string,
	{
		label: (num: number) => string
		datefn: any
	}
>

type StaticRange = {
	range: DateRange
	color: any | null
	isSelected(value: DateRange): boolean
	getRange(): DateRange
	label: string
}
type DateRangeMenuPanelProps = {
	maxDate?: Date
	minDate?: Date
	value: DateRange
	defaultValue: DateRange
	onValueChange: (range: DateRange) => void
	defaultPresets: Pick<StaticRange, 'getRange' | 'label'>[]
	localization?: Localization
}
type Localization = {
	applyText: string
	cancelText: string
	backText: string
	resetText: string
	absoluteDateText: string
	customRangePlaceholderText: string
	getDateFnsLocalization?(): any
}

const _localization: Localization = {
	applyText: 'Apply',
	cancelText: 'Cancel',
	backText: 'Back',
	resetText: 'Reset to default',
	absoluteDateText: 'Absolute date',
	customRangePlaceholderText: 'Custom range: 2d, 3w, 1h...',
	getDateFnsLocalization() {
		return undefined
	},
}

const staticRangeHandler: Partial<StaticRange> = {
	color: null,
	isSelected(value: DateRange): boolean {
		const definedRange = this.getRange?.()
		const { from, to } = value

		return (
			isSameDay(from as Date, definedRange?.from as Date) &&
			isSameDay(to as Date, definedRange?.to as Date)
		)
	},
}
function createStaticRange(ranges: Partial<StaticRange>[]): StaticRange[] {
	return ranges.map(
		(range: Partial<StaticRange>) =>
			({ ...staticRangeHandler, ...range }) as StaticRange
	)
}
const _defaultPresets: {
	label: string
	getRange(): DateRange
}[] = [
	{
		label: 'Current month',
		getRange: () => ({
			from: startOfMonth(new Date()),
			to: endOfDay(new Date()),
		}),
	},
	{
		label: 'Previous month',
		getRange: () => ({
			from: startOfDay(startOfMonth(addMonths(new Date(), -1))),
			to: endOfDay(endOfMonth(addMonths(new Date(), -1))),
		}),
	},
	{
		label: 'Last 7 days',
		getRange: () => ({
			from: startOfDay(addDays(new Date(), -7)),
			to: endOfDay(new Date()),
		}),
	},
	{
		label: 'Last 14 days',
		getRange: () => ({
			from: startOfDay(addDays(new Date(), -14)),
			to: endOfDay(new Date()),
		}),
	},
	{
		label: 'Last 30 days',
		getRange: () => ({
			from: startOfDay(addDays(new Date(), -30)),
			to: endOfDay(new Date()),
		}),
	},
	{
		label: 'Last 90 days',
		getRange: () => ({
			from: startOfDay(addDays(new Date(), -90)),
			to: endOfDay(new Date()),
		}),
	},
]
const formatForUser = (date: Date) => intlFormat(date)

const STATS_PERIOD_PATTERN = '^(\\d+)([dmwq])?$'

type ParsedString = {
	value: number
	unit: RelativePeriodUnit | null
}
/**
 * Parses a stats period into `period` and `periodLength`
 */
function parseStatsPeriod(input: string): ParsedString | undefined {
	const result = input.match(STATS_PERIOD_PATTERN)
	if (!result) {
		return undefined
	}
	const value = parseInt(result[1], 10)
	const unit = result?.[2] as RelativePeriodUnit

	return { value, unit }
}

const SUPPORTED_RELATIVE_PERIOD_UNITS: RelativeUnitsMapping = {
	d: {
		label: (num: number) => `Last ${num} days`,
		datefn: addDays,
	},
	w: {
		label: (num: number) => `Last ${num} weeks`,
		datefn: addWeeks,
	},
	m: {
		label: (num: number) => `Last ${num} months`,
		datefn: addMonths,
	},
	q: {
		label: (num: number) => `Last ${num} quarters`,
		datefn: addQuarters,
	},
}
function getSuggestedPeriods({ value, unit }: ParsedString) {
	const config =
		SUPPORTED_RELATIVE_PERIOD_UNITS[
			unit as keyof typeof SUPPORTED_RELATIVE_PERIOD_UNITS
		]
	if (!unit) {
		return Object.values(SUPPORTED_RELATIVE_PERIOD_UNITS).map((conf) => ({
			getRange: () => ({
				from: startOfDay(conf.datefn(new Date(), -value)),
				to: endOfDay(new Date()),
			}),
			label: conf.label(value),
		}))
	}

	return [
		{
			getRange: () => ({
				from: startOfDay(config.datefn(new Date(), -value)),
				to: endOfDay(new Date()),
			}),
			label: config.label(value),
		},
	]
}
function formatAbsoluteValue(value?: DateRange) {
	return `${value?.from ? formatForUser(value.from) : '_'} - ${value?.to ? formatForUser(value.to) : '_'}`
}
function DateRangeMenuPanel({
	value,
	onValueChange,
	defaultPresets = _defaultPresets,
	defaultValue,
	localization = _localization,
	minDate,
	maxDate,
}: DateRangeMenuPanelProps) {
	const [isAbsoluteDateShown, setIsAbsoluteDateShown] = useState(false)
	const [autocompleteText, setAutocompleteText] = useState('')
	const debouncedAutocompleteText = useDebounce<string>(autocompleteText, 1000)
	useEffect(() => {
		if (debouncedAutocompleteText !== '') {
			try {
				setSuggestedRanges(
					getSuggestedPeriods(parseStatsPeriod(debouncedAutocompleteText)!)
				)
			} catch (error) {
				setSuggestedRanges(defaultPresets)
			}
		} else {
			setSuggestedRanges(defaultPresets)
		}
	}, [debouncedAutocompleteText])
	const [suggestedRanges, setSuggestedRanges] = useState(defaultPresets)
	const ranges: StaticRange[] = useMemo(
		() => createStaticRange(suggestedRanges),
		[suggestedRanges]
	)
	const definedRange = useMemo(
		() => ranges.find((range: StaticRange) => range.isSelected(value)),
		[ranges, value]
	)

	return isAbsoluteDateShown ? (
		<AbsoluteDateRange
			value={value}
			minDate={minDate}
			maxDate={maxDate}
			localization={localization}
			onBack={() => setIsAbsoluteDateShown(false)}
			onReset={() => onValueChange(defaultValue)}
			onApply={(range) => {
				setIsAbsoluteDateShown(false)
				onValueChange(range)
			}}
		/>
	) : (
		<Command>
			<CommandList>
				<CommandGroup>
					<CommandItem
						className='justify-between text-sm font-semibold'
						onSelect={() => setIsAbsoluteDateShown(true)}
					>
						<CheckIcon
							className={cn(
								value && !definedRange ? 'opacity-100' : 'opacity-0'
							)}
						/>
						<span>
							{value?.from && value?.to
								? formatAbsoluteValue(value)
								: localization.absoluteDateText}
						</span>
						<ArrowRightIcon className='size-4' />
					</CommandItem>

					{ranges.map((range) => (
						<CommandItem
							key={range.label}
							onSelect={() => {
								setIsAbsoluteDateShown(false)
								onValueChange(range.getRange())
							}}
						>
							<CheckIcon
								className={cn(
									value.from && value.to && range?.isSelected(value)
										? 'opacity-100'
										: 'opacity-0'
								)}
							/>
							<span>{range.label}</span>
						</CommandItem>
					))}
				</CommandGroup>
				<CommandGroup>
					<div className='flex items-center'>
						<Input
							className='bg-input/20 dark:bg-input/30'
							value={autocompleteText}
							onChange={(e) => setAutocompleteText(e.target.value)}
							placeholder={localization.customRangePlaceholderText}
						/>
					</div>
				</CommandGroup>
			</CommandList>
		</Command>
	)
}
function AbsoluteDateRange({
	value,
	onApply,
	onBack,
	onReset,
	localization,
	minDate,
	maxDate,
}: {
	value?: DateRange
	onApply: (range: DateRange) => void
	onBack: () => void
	onReset: () => void
	localization: Localization
	minDate?: Date
	maxDate?: Date
}) {
	const [date, setDate] = React.useState<DateRange | undefined>(
		value || undefined
	)

	return (
		<div className='flex flex-col'>
			<Calendar
				disabled={
					[
						minDate
							? {
									before: minDate,
								}
							: undefined,
						maxDate
							? {
									after: maxDate,
								}
							: undefined,
					].filter(Boolean) as Matcher[]
				}
				locale={localization?.getDateFnsLocalization?.()}
				fixedWeeks
				autoFocus
				data-slot='date-picker-calendar'
				mode='range'
				selected={date === null ? undefined : date}
				onSelect={(value: Date | Date[] | DateRange | undefined) => {
					if (!value) {
						setDate(undefined)
					}
					setDate(value as DateRange)
				}}
			/>

			<div className='flex gap-2 p-3'>
				<Button size='sm' className='mr-auto' onClick={onReset}>
					{localization.resetText}
				</Button>
				<Button size='sm' onClick={onBack}>
					<ArrowLeftIcon />
					{localization.backText}
				</Button>
				<Button
					variant='primary'
					size='sm'
					disabled={!date || !date.from || !date.to}
					onClick={() => {
						date && date.from && date.to && onApply(date)
					}}
				>
					{localization.applyText}
				</Button>
			</div>
		</div>
	)
}

type UseSelectedPresetProps = {
	value: DateRange
	staticRanges: StaticRange[]
	locale?: Localization
}
function useRangePreset({
	value,
	staticRanges,
}: UseSelectedPresetProps): StaticRange | null {
	const defaultRange = useMemo(
		() =>
			value && value.from && value.to
				? ({
						range: value,
						getRange: () => value,
						isSelected: () => true,
						label: formatAbsoluteValue(value),
						color: null,
					} as StaticRange)
				: null,
		[value]
	)
	const definedRange = useMemo(
		() => staticRanges.find((range: StaticRange) => range.isSelected(value)),
		[staticRanges, value]
	)

	return definedRange || defaultRange
}

export {
	DateRangeMenuPanel,
	createStaticRange,
	_defaultPresets as defaultPresets,
	useRangePreset,
}
